import { useEffect, useState } from 'react';
import { MdDownload, MdRefresh, MdRemoveRedEye, MdSearch } from 'react-icons/md';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import { calculatePaymentStats, subscribeToPayments, deletePayment, updatePayment } from '../../services/paymentService';

/* ─── Helpers ────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  success: { label: 'Success', bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
  pending: { label: 'Pending', bg: 'bg-amber-100', text: 'text-amber-800', dot: 'bg-amber-500' },
  failed:  { label: 'Failed',  bg: 'bg-red-100',   text: 'text-red-800',   dot: 'bg-red-500' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.failed;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const formatPaymentDate = (dateVal, options) => {
  if (!dateVal) return 'N/A';
  try {
    const d = dateVal.toDate ? dateVal.toDate() : new Date(dateVal);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-US', options || { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return 'N/A';
  }
};

/* ─── Mobile Payment Card ───────────────────────────────── */
const MobileCard = ({ payment, onView }) => {
  const studentName = payment.student || 'Unknown Student';
  const tId = payment.transactionId || payment.id;
  return (
  <div className="bg-white rounded-xl border border-gray-200 p-3 md:p-4 shadow-sm mb-3">
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-semibold text-base md:text-lg flex-shrink-0">
          {studentName.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm md:text-base truncate">
            {studentName}
          </h3>
          <p className="text-xs text-gray-500 font-mono truncate">{tId}</p>
        </div>
      </div>
      
      {/* Action Button */}
      <button
        onClick={() => onView(payment)}
        className="w-10 h-10 flex items-center justify-center bg-primary text-white rounded-xl hover:bg-blue-700 transition-colors flex-shrink-0"
        title="View details"
      >
        <MdRemoveRedEye className="text-lg" />
      </button>
    </div>
  </div>
)};

const Payments = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    success: 0,
    pending: 0,
    failed: 0,
    totalRevenue: 0,
    todayRevenue: 0,
    monthlyRevenue: 0,
  });

  // Subscribe to real-time payment updates
  useEffect(() => {
    setLoading(true);
    const unsubscribe = subscribeToPayments((data) => {
      setPayments(data);
      setStats(calculatePaymentStats(data));
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setShowViewModal(true);
  };

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 500);
  };

  const handleExport = () => {
    console.log('Exporting payments to PDF...');
    // Implement PDF export logic here
  };

  /* Filter */
  const filtered = payments.filter((p) => {
    const matchTab = activeTab === 'all' || p.status === activeTab;
    if (!matchTab) return false;
    if (!searchTerm) return true;

    const term = searchTerm.toLowerCase();
    const tId = (p.transactionId || p.id || '').toLowerCase();
    const sName = (p.student || '').toLowerCase();
    const sEmail = (p.email || '').toLowerCase();

    return tId.includes(term) || sName.includes(term) || sEmail.includes(term);
  });

  // Table columns
  const columns = [
    {
      header: 'Transaction ID',
      accessor: 'transactionId',
      render: (value, row) => <span className="font-medium text-gray-900 font-mono text-sm">{value || row.id}</span>,
    },
    {
      header: 'Student',
      accessor: 'student',
      render: (value, row) => {
        const studentName = value || 'Unknown Student';
        return (
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
               {studentName.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">{studentName}</p>
              <p className="text-xs text-gray-500">{row.email}</p>
            </div>
          </div>
        );
      },
    },
    {
      header: 'Subject/Plan',
      accessor: 'subject',
      render: (value, row) => (
        <div>
          <p className="text-sm font-medium text-gray-800">{value || 'N/A'}</p>
          <p className="text-xs text-gray-500">{row.plan || 'N/A'}</p>
        </div>
      ),
    },
    {
      header: 'Amount',
      accessor: 'amount',
      render: (value) => <span className="text-gray-900 font-bold text-sm">{value}</span>,
    },
    {
      header: 'Gateway',
      accessor: 'paymentGateway',
      render: (value) => (
        <span className="text-xs font-semibold px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg">{value}</span>
      ),
    },
    {
      header: 'Date',
      accessor: 'date',
      render: (value, row) => (
        <span className="text-sm text-gray-600">
          {formatPaymentDate(value || row.paidAt || row.createdAt)}
        </span>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (value) => <StatusBadge status={value} />,
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: (value, row) => (
        <button
          onClick={() => handleViewPayment(row)}
          className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-primary hover:text-white transition-all"
          title="View details"
        >
          <MdRemoveRedEye size={17} />
        </button>
      ),
    },
  ];

  const tabs = [
    { id: 'all', label: 'All Payments', count: stats.total },
    { id: 'success', label: 'Successful', count: stats.success },
    { id: 'pending', label: 'Pending', count: stats.pending },
    { id: 'failed', label: 'Failed', count: stats.failed },
  ];

  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-darkNavy mb-1 md:mb-2 font-poppins">Payments</h1>
          <p className="text-sm md:text-base text-gray-500">Track all payment transactions</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all text-sm md:text-base shadow-sm disabled:opacity-50"
          >
            <MdRefresh className={`text-lg ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all text-sm md:text-base shadow-sm"
          >
            <MdDownload className="text-lg" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
        <div className="bg-white p-3 md:p-5 rounded-xl md:rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-center">
          <p className="text-[10px] md:text-sm text-gray-500 mb-1">Total Revenue</p>
          <p className="text-lg md:text-2xl font-bold text-darkNavy">₦{stats.totalRevenue.toLocaleString()}</p>
          <p className="text-[9px] md:text-xs text-emerald-600 mt-1 font-semibold">This month: ₦{stats.monthlyRevenue.toLocaleString()}</p>
        </div>
        <div className="bg-white p-3 md:p-5 rounded-xl md:rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-center">
          <p className="text-[10px] md:text-sm text-gray-500 mb-1">Successful</p>
          <p className="text-lg md:text-2xl font-bold text-emerald-600">{stats.success.toLocaleString()}</p>
          <p className="text-[9px] md:text-xs text-gray-400 mt-1">{stats.total > 0 ? ((stats.success / stats.total) * 100).toFixed(1) : 0}% success rate</p>
        </div>
        <div className="bg-white p-3 md:p-5 rounded-xl md:rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-center">
          <p className="text-[10px] md:text-sm text-gray-500 mb-1">Pending</p>
          <p className="text-lg md:text-2xl font-bold text-amber-600">{stats.pending.toLocaleString()}</p>
          <p className="text-[9px] md:text-xs text-gray-400 mt-1">Awaiting confirmation</p>
        </div>
        <div className="bg-white p-3 md:p-5 rounded-xl md:rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-center">
          <p className="text-[10px] md:text-sm text-gray-500 mb-1">Failed</p>
          <p className="text-lg md:text-2xl font-bold text-red-600">{stats.failed.toLocaleString()}</p>
          <p className="text-[9px] md:text-xs text-gray-400 mt-1">{stats.total > 0 ? ((stats.failed / stats.total) * 100).toFixed(1) : 0}% failure rate</p>
        </div>
      </div>

      {/* Tabs and Filters Container */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-5">
        {/* Tabs */}
        <div className="border-b border-gray-100 overflow-x-auto scrollbar-hide">
          <div className="flex min-w-max px-2 pt-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex items-center gap-2 px-4 py-3 font-semibold whitespace-nowrap text-sm transition-colors flex-shrink-0 border-b-2 ${
                  activeTab === tab.id
                    ? 'text-primary border-primary'
                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50 rounded-t-lg'
                }`}
              >
                {tab.label}
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === tab.id
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {tab.count.toLocaleString()}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 p-4">
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-full sm:w-auto px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm transition-all"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>

          <div className="relative flex-1">
            <MdSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search by transaction ID or student..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm"
            />
          </div>
        </div>
      </div>

      {/* Results count */}
      <p className="text-xs text-gray-400 mb-3 px-1">
        Showing <span className="font-semibold text-gray-600">{filtered.length}</span> payment{filtered.length !== 1 ? 's' : ''}
      </p>

      {/* ── Mobile Cards (< lg) ── */}
      <div className="lg:hidden space-y-3">
        {loading ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
            <MdRefresh className="text-5xl text-primary mx-auto mb-3 animate-spin" />
            <p className="text-gray-400 text-sm">Loading payments...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
             <p className="text-gray-400 text-sm">No payments found</p>
          </div>
        ) : (
          filtered.map((payment) => (
            <MobileCard key={payment.id} payment={payment} onView={handleViewPayment} />
          ))
        )}
      </div>

      {/* ── Desktop Table (≥ lg) ── */}
      <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
           <div className="p-16 text-center">
             <MdRefresh className="text-6xl text-primary mx-auto mb-4 animate-spin" />
             <p className="text-gray-500 font-medium">Loading payments...</p>
           </div>
        ) : filtered.length === 0 ? (
           <div className="p-16 text-center">
             <p className="text-gray-400">No payments found</p>
           </div>
        ) : (
          <div className="overflow-x-auto">
            <DataTable columns={columns} data={filtered} />
          </div>
        )}
      </div>

      {/* View Payment Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedPayment(null);
        }}
        title="Payment Details"
        size="medium"
      >
        {selectedPayment && (
          <div className="space-y-6">
            {/* Payment Status Badge */}
            <div className="text-center pb-5 border-b border-gray-100">
              <span className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold ${
                selectedPayment.status === 'success' ? 'bg-green-100 text-green-800' :
                selectedPayment.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                'bg-red-100 text-red-800'
              }`}>
                {selectedPayment.status === 'success' ? '✓ Payment Successful' :
                 selectedPayment.status === 'pending' ? '⏳ Payment Pending' :
                 '✗ Payment Failed'}
              </span>
            </div>

            {/* Payment Information Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded-xl">
                <label className="block text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Transaction ID</label>
                <p className="text-sm md:text-base text-gray-900 font-mono font-medium">{selectedPayment.transactionId || selectedPayment.id}</p>
              </div>

              <div className="bg-gray-50 p-3 rounded-xl">
                <label className="block text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Amount</label>
                <p className="text-lg md:text-xl text-emerald-600 font-bold">{selectedPayment.amount}</p>
              </div>

              <div className="bg-gray-50 p-3 rounded-xl sm:col-span-2">
                <label className="block text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Student</label>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                    {(selectedPayment.student || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div>
                     <p className="text-sm md:text-base text-gray-900 font-bold">{selectedPayment.student || 'Unknown Student'}</p>
                     <p className="text-xs text-gray-500">{selectedPayment.email}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-3 rounded-xl">
                <label className="block text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Subject/Plan</label>
                <p className="text-sm text-gray-900 font-medium">{selectedPayment.subject || 'N/A'}</p>
                <p className="text-xs text-gray-500">{selectedPayment.plan || 'N/A'}</p>
              </div>

              <div className="bg-gray-50 p-3 rounded-xl">
                <label className="block text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Payment Gateway</label>
                <p className="text-sm text-gray-900 font-medium">{selectedPayment.paymentGateway || 'Paystack'}</p>
              </div>

              <div className="bg-gray-50 p-3 rounded-xl sm:col-span-2">
                <label className="block text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Payment Reference</label>
                <p className="text-xs md:text-sm text-gray-900 font-medium font-mono">{selectedPayment.paymentReference || selectedPayment.id}</p>
              </div>

              <div className="bg-gray-50 p-3 rounded-xl sm:col-span-2">
                <label className="block text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Transaction Date</label>
                <p className="text-sm text-gray-900 font-medium">
                  {formatPaymentDate(selectedPayment.paidAt || selectedPayment.createdAt || selectedPayment.date, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-3 border-t border-gray-100">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedPayment(null);
                }}
                className="w-full sm:w-auto px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors text-sm"
              >
                Close
              </button>
              
              {selectedPayment.status === 'pending' && (
                <>
                  <button
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to delete this pending payment?')) {
                        try {
                          await deletePayment(selectedPayment.id);
                          setShowViewModal(false);
                          setSelectedPayment(null);
                          alert('Payment deleted successfully');
                        } catch (error) {
                          alert('Failed to delete payment');
                        }
                      }
                    }}
                    className="w-full sm:w-auto px-6 py-2.5 bg-red-50 text-red-600 rounded-xl font-semibold hover:bg-red-100 transition-colors text-sm"
                  >
                    Delete Payment
                  </button>
                  <button
                    onClick={async () => {
                      if (window.confirm('Manually mark this payment as Successful? This is usually done automatically by Paystack.')) {
                        try {
                          await updatePayment(selectedPayment.id, { status: 'success' });
                          setShowViewModal(false);
                          setSelectedPayment(null);
                          alert('Payment marked as successful');
                        } catch (error) {
                          alert('Failed to update payment');
                        }
                      }
                    }}
                    className="w-full sm:w-auto px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors text-sm"
                  >
                    Mark as Successful
                  </button>
                </>
              )}

              {selectedPayment.status === 'success' && (
                <button
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-blue-700 transition-all text-sm shadow-sm"
                >
                  <MdDownload className="text-lg" />
                  Download Receipt
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Payments;
